/**
 * NFA Analytics — ultra-detailed, fully interactive dashboard.
 * Every widget is clickable and drills L1 → L2 → L3 (note) → L4 (ladder).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Area, Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart,
         ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Activity, AlertTriangle, BellRing, Layers, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNfaTheme, money } from './nfaTheme';
import { Btn, Empty, Panel, Stat } from './NfaPrimitives';
import { NfaRegisterSweep } from './NfaCognara';
import NfaDrilldownDrawer from './NfaDrilldownDrawer';
import NfaDetailDrawer from './NfaDetailDrawer';

const num = (v, s = '') => (v === null || v === undefined ? '—' : `${v}${s}`);

const DrillChip = ({ T, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-1 text-[9.5px] px-1.5 py-0.5 rounded-full font-semibold tracking-wide"
    style={{ background: T.tint(T.primary), color: T.primary }}>
    <Layers className="w-3 h-3" /> L1 · L2 · L3 · L4
  </button>
);

const RankedList = ({ w, T, onDrill, tone }) => {
  const max = Math.max(1, ...w.rows.map((r) => Math.abs(r.value || r.count || 0)));
  if (!w.rows.length) return <Empty T={T}>No data in this slice yet.</Empty>;
  return (
    <div className="space-y-1.5">
      {w.rows.map((r) => (
        <button type="button" key={r.key} data-testid={`nfa-wx-${w.widget}-${r.key}`}
          onClick={() => onDrill(w.widget, [r.key])}
          className="w-full text-left group">
          <div className="flex items-center gap-2 text-[10.5px]">
            <span className="truncate font-medium" style={{ color: T.ink }}>{r.label}</span>
            {!!r.overdue && (
              <span className="shrink-0 text-[9px] px-1.5 rounded-full"
                style={{ background: T.tint(T.red), color: T.red }}>{r.overdue} overdue</span>
            )}
            <span className="ml-auto shrink-0 tabular-nums" style={{ color: T.muted }}>
              {w.kind === 'level' ? `${r.count}` : `${r.notes}`}
              {w.widget === 'cycleByLevel' && r.avgCycleDays != null ? ` · ${r.avgCycleDays}d` : ''}
              {' · '}{money(r.value)}
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden mt-0.5" style={{ background: T.surfaceAlt }}>
            <div className="h-full rounded-full transition-all group-hover:opacity-80"
              style={{ width: `${Math.round((Math.abs(
                w.widget === 'cycleByLevel' ? (r.avgCycleDays || 0) : (r.value || r.count)) /
                (w.widget === 'cycleByLevel'
                  ? Math.max(1, ...w.rows.map((x) => x.avgCycleDays || 0))
                  : max)) * 100)}%`,
                background: r.overdue ? T.red : tone }} />
          </div>
        </button>
      ))}
    </div>
  );
};

const Donut = ({ w, T, onDrill, palette }) => {
  const data = w.rows.filter((r) => r.count);
  if (!data.length) return <Empty T={T}>No data in this slice yet.</Empty>;
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div style={{ width: 160, height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="label" innerRadius={42} outerRadius={70}
              paddingAngle={2} onClick={(e) => e?.payload && onDrill(w.widget, [e.payload.key])}>
              {data.map((r, i) => <Cell key={r.key} fill={palette[i % palette.length]} cursor="pointer" />)}
            </Pie>
            <Tooltip contentStyle={{ background: T.surface, border: `1px solid ${T.border}`,
              fontSize: 11, color: T.ink }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 min-w-[180px] space-y-1">
        {data.map((r, i) => (
          <button type="button" key={r.key} data-testid={`nfa-wx-${w.widget}-${r.key}`}
            onClick={() => onDrill(w.widget, [r.key])}
            className="w-full flex items-center gap-2 text-[10.5px] hover:opacity-80">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: palette[i % palette.length] }} />
            <span className="truncate" style={{ color: T.ink }}>{r.label}</span>
            <span className="ml-auto tabular-nums shrink-0" style={{ color: T.muted }}>
              {r.count} · {money(r.value)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const Funnel = ({ w, T, onDrill }) => {
  const top = Math.max(1, ...w.rows.map((r) => r.count));
  return (
    <div className="space-y-1.5">
      {w.rows.map((r, i) => (
        <button type="button" key={r.key} data-testid={`nfa-wx-${w.widget}-${r.key}`}
          onClick={() => onDrill(w.widget, [r.key])} className="w-full text-left">
          <div className="flex items-center gap-2 text-[10.5px]">
            <span style={{ color: T.ink }}>{r.label}</span>
            <span className="ml-auto tabular-nums" style={{ color: T.muted }}>
              {r.count} · {money(r.value)} · {Math.round((r.count / top) * 100)}%</span>
          </div>
          <div className="h-5 rounded mt-0.5 flex items-center px-2 text-[9.5px] font-semibold"
            style={{ width: `${Math.max(12, (r.count / top) * 100)}%`,
                     background: [T.primaryDeep, T.primary, T.primaryLight, T.amber, T.green][i] || T.primary,
                     color: '#fff' }}>{r.count}</div>
        </button>
      ))}
    </div>
  );
};

const TrendChart = ({ trend, T, onDrill }) => {
  if (!trend?.length) return <Empty T={T}>No dated notes yet.</Empty>;
  return (
    <div style={{ height: 230 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={trend} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}
          onClick={(e) => e?.activeLabel && onDrill('byMonth', [e.activeLabel])}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
          <XAxis dataKey="month" tick={{ fill: T.muted, fontSize: 10 }} />
          <YAxis yAxisId="l" tick={{ fill: T.muted, fontSize: 10 }} allowDecimals={false} />
          <YAxis yAxisId="r" orientation="right" tick={{ fill: T.muted, fontSize: 10 }}
            tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} />
          <Tooltip contentStyle={{ background: T.surface, border: `1px solid ${T.border}`,
            fontSize: 11, color: T.ink }}
            formatter={(v, n) => (String(n).includes('Value') ? money(v) : v)} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <Area yAxisId="r" type="monotone" dataKey="raisedValue" name="Raised value"
            stroke={T.primaryLight} fill={T.tint(T.primaryLight, '33')} />
          <Bar yAxisId="l" dataKey="raised" name="Raised" fill={T.primary} radius={[3, 3, 0, 0]} cursor="pointer" />
          <Bar yAxisId="l" dataKey="approved" name="Approved" fill={T.green} radius={[3, 3, 0, 0]} cursor="pointer" />
          <Line yAxisId="l" type="monotone" dataKey="rejected" name="Rejected" stroke={T.red} strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const NfaWidgetCard = ({ w, T, onDrill, trend }) => {
  const palette = [T.primary, T.primaryLight, T.accent, T.amber, T.green, T.red,
                   T.primaryDeep, '#7E57C2', '#26A69A', '#8D6E63'];
  const tone = { pendingByLevel: T.amber, roleWorkload: T.accent, cycleByLevel: T.primaryLight }[w.widget]
    || T.primary;
  return (
    <Panel T={T} title={w.title} subtitle={w.subtitle} testId={`nfa-widget-${w.widget}`}
      right={<DrillChip T={T} testId={`nfa-widget-drill-${w.widget}`}
        onClick={() => onDrill(w.widget, [], 'chain')} />}>
      {w.chart === 'donut' && <Donut w={w} T={T} onDrill={onDrill} palette={palette} />}
      {w.chart === 'funnel' && <Funnel w={w} T={T} onDrill={onDrill} />}
      {w.chart === 'area' && <TrendChart trend={trend} T={T} onDrill={onDrill} />}
      {(w.chart === 'bar' || w.chart === 'progress') && (
        <RankedList w={w} T={T} onDrill={onDrill} tone={tone} />
      )}
      <p className="mt-2 text-[9.5px]" style={{ color: T.muted }}>
        Click any {w.dimensionLabel.toLowerCase()} to jump straight to its notes · the L1·L2·L3·L4 chip opens the full breakdown
      </p>
    </Panel>
  );
};

export const NfaWidgetGrid = ({ widgets, only, T, projectId, trend }) => {
  const [drill, setDrill] = useState(null);
  const shown = useMemo(() => {
    const map = Object.fromEntries((widgets || []).map((w) => [w.widget, w]));
    return (only || (widgets || []).map((w) => w.widget)).map((id) => map[id]).filter(Boolean);
  }, [widgets, only]);
  const onDrill = useCallback((widget, path, mode = 'notes') => setDrill({ widget, path, mode }), []);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {shown.map((w) => (
          <div key={w.widget} className={w.chart === 'area' ? 'lg:col-span-2' : ''}>
            <NfaWidgetCard w={w} T={T} onDrill={onDrill} trend={trend} />
          </div>
        ))}
      </div>
      {drill && (
        <NfaDrilldownDrawer widget={drill.widget} initialPath={drill.path} mode={drill.mode}
          projectId={projectId} onClose={() => setDrill(null)} />
      )}
    </>
  );
};

export const useNfaOverview = (projectId) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(true);
  const load = useCallback(() => {
    if (!projectId) return;            // hard scope: never read across projects
    setBusy(true);
    api.get(`/nfa/analytics/overview?project_id=${projectId}`)
      .then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setBusy(false));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  return { d, busy, reload: load };
};

const NfaAnalytics = ({ projectId, projectName }) => {
  const T = useNfaTheme();
  const { d, busy, reload } = useNfaOverview(projectId);
  const [drill, setDrill] = useState(null);
  const [openNote, setOpenNote] = useState(null);
  if (busy && !d) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!d) return <Panel T={T}><Empty T={T}>Analytics could not be loaded for this project.</Empty></Panel>;
  const k = d.kpis;
  const go = (view) => setDrill({ view });

  const KPIS = [
    ['Notes in register', k.notes, `${k.draft} draft · ${k.open} live`, T.ink, 'all_notes'],
    ['Total value', money(k.value), `avg ${money(k.avgValue)}`, T.ink, 'by_value'],
    ['Open value', money(k.openValue), `${k.open} notes under review`, T.amber, 'open'],
    ['Value at risk', money(k.valueAtRisk), `${k.notesAtRisk} note(s) past SLA`,
     k.valueAtRisk ? T.red : T.green, 'at_risk'],
    ['SLA breaches', k.slaBreaches, `compliance ${num(k.slaCompliance, '%')}`,
     k.slaBreaches ? T.red : T.green, 'sla_breach'],
    ['Bottleneck level', k.bottleneckLevel ? `L${k.bottleneckLevel.level_no}` : '—',
     k.bottleneckLevel ? `${k.bottleneckLevel.avgDays}d avg · ${k.bottleneckLevel.samples} samples` : null,
     T.accent, 'bottleneck'],
    ['Avg cycle time', num(k.avgCycleDays, 'd'), `median ${num(k.medianCycleDays, 'd')} · slowest ${num(k.slowestCycleDays, 'd')}`,
     T.ink, 'slow_cycle'],
    ['First-pass yield', num(k.firstPassYield, '%'), `approval rate ${num(k.approvalRate, '%')}`,
     T.green, 'first_pass'],
    ['Returned / rejected', `${k.returnedCount} / ${k.rejectedCount}`,
     `rejection rate ${num(k.rejectionRate, '%')}`, T.red, 'returned_rejected'],
    ['Approved, not committed', k.approvedNotCommitted, money(k.approvedNotCommittedValue),
     T.primary, 'approved_not_committed'],
    ['Oldest pending', num(k.oldestPendingDays, 'd'), `${k.pendingOver14} beyond 14 days`,
     k.oldestPendingDays > 14 ? T.red : T.ink, 'oldest_pending'],
    ['Evidence coverage', num(k.attachmentCoverage, '%'), `avg ${num(k.avgLevelsPerNote)} levels per note`,
     T.ink, 'no_evidence'],
  ];

  return (
    <div className="space-y-4" data-testid="nfa-analytics">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[10.5px] px-2 py-1 rounded-md"
          style={{ background: T.tint(T.primary), color: T.primary }} data-testid="nfa-analytics-scope">
          <Activity className="w-3.5 h-3.5" /> {projectName || projectId} · {k.notes} notes analysed
        </span>
        {!!k.slaBreaches && (
          <span className="inline-flex items-center gap-1.5 text-[10.5px] px-2 py-1 rounded-md"
            style={{ background: T.tint(T.red), color: T.red }} data-testid="nfa-analytics-alert">
            <AlertTriangle className="w-3.5 h-3.5" /> {k.slaBreaches} level(s) past SLA ·
            {' '}{money(k.valueAtRisk)} at risk
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Btn T={T} variant="warn" testId="nfa-analytics-remind" onClick={async () => {
            try {
              const r = await api.post(`/nfa/assist/reminders/run?project_id=${projectId || ''}`);
              toast.success(`${r.data.sentCount} reminder(s) sent · ${r.data.skipped} skipped`);
            } catch (e) { toast.error(e?.response?.data?.detail || 'Could not run the reminder sweep'); }
          }}><BellRing className="w-3.5 h-3.5" /> Nudge overdue approvers</Btn>
          <Btn T={T} variant="ghost" testId="nfa-analytics-refresh" onClick={reload}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Btn>
        </div>
      </div>

      <NfaRegisterSweep T={T} projectId={projectId} projectName={projectName}
        onOpenNfa={(id) => setOpenNote(id)} />

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        {KPIS.map(([label, value, sub, tone, view]) => (
          <button type="button" key={label} onClick={() => go(view)}
            data-testid={`nfa-kpi-${String(label).toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className="text-left hover:opacity-85 transition-opacity">
            <Stat T={T} label={label} value={value} sub={sub} tone={tone} />
          </button>
        ))}
      </div>

      {k.largestNote && (
        <Panel T={T} title="Largest single commitment in the register" testId="nfa-largest-note"
          subtitle="Open it to inspect the full ladder and evidence">
          <button type="button" onClick={() => setOpenNote(k.largestNote.nfa_id)}
            data-testid="nfa-largest-open" className="w-full text-left flex items-center gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-mono text-[10px]" style={{ color: T.primary }}>{k.largestNote.nfa_no}</div>
              <div className="text-[12.5px] font-semibold" style={{ color: T.ink }}>{k.largestNote.title}</div>
              <div className="text-[10px]" style={{ color: T.muted }}>
                {k.largestNote.nfa_type} · {k.largestNote.vendor} · WBS {k.largestNote.wbs} ·
                {' '}{k.largestNote.status}
              </div>
            </div>
            <span className="ml-auto text-[17px] font-semibold tabular-nums" style={{ color: T.ink }}>
              {money(k.largestNote.value, k.largestNote.currency)}
            </span>
            <Search className="w-4 h-4" style={{ color: T.muted }} />
          </button>
        </Panel>
      )}

      <NfaWidgetGrid widgets={d.widgets} T={T} projectId={projectId} trend={d.trend} />

      {drill && (
        <NfaDrilldownDrawer view={drill.view} projectId={projectId}
          onClose={() => setDrill(null)} />
      )}
      {openNote && <NfaDetailDrawer nfaId={openNote} onClose={() => setOpenNote(null)} onChanged={reload} />}
    </div>
  );
};

export default NfaAnalytics;
